import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
    addConfig,
    listConfigs,
    removeConfig,
    updateConfig
} from '@/api/system-config'
import IApiResponse from '@/types/api-response'
import IList from '@/types/list-result'
import IPagination from '@/types/pagination'

interface ISystemConfigState {
	configs: ISystemConfig[]
	loading: boolean
	error: string | null
	pagination?: IPagination
}

export interface ISystemConfig {
	_id: string
    key: string
	value: string
    type: 'string' | 'boolean' | 'array' | 'object' | 'number'
    description?: string
    updated_at?: Date
    updated_by?: string
    created_at: Date
    created_by: string
}

const initialState: ISystemConfigState = {
	loading: false,
	configs: [],
	error: null
}

export const fetchSystemConfigs = createAsyncThunk<
	IApiResponse<IList<ISystemConfig>>,
	any
>('system_config/fetch_system_config', async (params: any) => {
	return await listConfigs(params)
})

export const updateSystemConfig = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('system_config/update_system_config', async ({ params, cb }: any) => {
	try {
        const allowProps = ['_id', 'key', 'value', 'type', 'description']
        Object.keys(params).forEach(key => {
            if (!allowProps.includes(key)) {
                delete params[key]
            }
        }) 
		const result = await updateConfig(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error: any) {
		if (typeof cb === 'function') {
			cb({ success: false, message: error?.response?.data?.message || error.message || 'Đã xảy ra lỗi' })
		}

		throw error
	}
})

export const removeSystemConfig= createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('system_config/remove_system_config', async ({ params, cb }: any) => {
	try {
		const result = await removeConfig(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error: any) {
		if (typeof cb === 'function') {
			cb({ success: false, message: error?.response?.data?.message || error.message || 'Đã xảy ra lỗi' })
		}

		throw error
	}
})

export const addSystemConfig = createAsyncThunk<
	IApiResponse<null | undefined>,
	any
>('system_config/add_system_config', async ({ params, cb }: any) => {
	try {
        const allowProps = ['_id', 'key', 'value', 'type', 'description']
        Object.keys(params).forEach(key => {
            if (!allowProps.includes(key)) {
                delete params[key]
            }
        }) 
        
		const result = await addConfig(params)

		if (typeof cb === 'function') {
			cb(result)
		}

		return result
	} catch (error: any) {
		if (typeof cb === 'function') {
			cb({ success: false, message: error?.response?.data?.message || error.message || 'Đã xảy ra lỗi' })
		}

		throw error
	}
})

const systemConfigSlice = createSlice({
	name: 'system_config',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSystemConfigs.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(fetchSystemConfigs.fulfilled, (state, { payload }) => {
				state.loading = false
				state.pagination = payload.data!.pagination
				if (payload.success) {
					state.configs = payload.data!.records
				} else {
					state.configs = []
				}
			})
			.addCase(fetchSystemConfigs.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message || 'Something went wrong'
			})
	},
})

export default systemConfigSlice.reducer
